/**
 * Corvee taken (template) — Admin → Applicatiebeheer.
 * Beheert het standaard takentemplate (dagtaken, weektaken, personen/dag) in
 * corvee_config (PostgreSQL via AI Engine). In de corveetool kun je dit per
 * planning tweaken of resetten naar dit template.
 */
import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, CircularProgress } from "@mui/material";
import { SectionHeader, T, Icon } from "../adminSettingsCommon";
import CorveeTaskEditor from "../../Corvee/CorveeTaskEditor";
import { DEFAULT_DAILY, DEFAULT_WEEKLY, DEFAULT_PER_DAY } from "../../Corvee/corveeLogic";
import { BACKEND_URL } from "../../../utils/apiConfig";
import { authFetch } from "../../../utils/authHelpers";
import { leesJson, meldFout } from "../../../utils/apiFout";

const API = `${BACKEND_URL}/Api/corvee/config`;
const toTpl = (c) => ({ dailyTasks: c?.daily_tasks || [], weeklyTasks: c?.weekly_tasks || [], perDay: c?.per_day || [2, 2, 2, 2, 2], teamSize: c?.team_size || 6 });
const toBody = (t) => ({ daily_tasks: t.dailyTasks, weekly_tasks: t.weeklyTasks, per_day: t.perDay, team_size: t.teamSize });

export default function CorveeTasksConfig() {
  const [tpl, setTpl] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { (async () => {
    try { const c = await authFetch(API).then((r) => leesJson(r, "Corvee-template laden")); setTpl(toTpl(c)); }
    catch (e) { console.error("[Corvee] config laden:", e); meldFout(e, "Corvee-template laden"); setTpl(toTpl(null)); }
  })(); }, []);

  const persist = async (next) => {
    setTpl(next);
    try {
      await authFetch(API, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(toBody(next)) });
      setSaved(true); setTimeout(() => setSaved(false), 1500);
    } catch (e) { console.error("[Corvee] config opslaan:", e); }
  };
  const doReset = () => persist({ dailyTasks: DEFAULT_DAILY, weeklyTasks: DEFAULT_WEEKLY, perDay: DEFAULT_PER_DAY, teamSize: 6 });

  return (
    <Box>
      <SectionHeader
        title="Corvee taken (template)"
        subtitle="Het standaard takentemplate voor het corveerooster. In de corveetool kan dit per planning getweakt of teruggezet worden naar dit template."
      />
      {!tpl ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={26} /></Box>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box onClick={doReset} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.85, borderRadius: "0.55rem", cursor: "pointer", border: `1.5px solid rgba(45,51,53,0.12)`, color: T.secondary, fontFamily: T.fontBody, fontWeight: 600, fontSize: "0.8rem", "&:hover": { bgcolor: T.surfaceLow } }}>
              <Icon name="restart_alt" size={16} /> Terug naar fabrieksinstelling
            </Box>
            {saved && <Typography sx={{ fontFamily: T.fontBody, fontSize: "0.74rem", color: T.emerald, display: "inline-flex", alignItems: "center", gap: 0.3 }}><Icon name="check" size={15} sx={{ color: T.emerald }} /> Opgeslagen</Typography>}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: "0.82rem", fontWeight: 600, color: T.text }}>Max weekteam per week</Typography>
            <TextField size="small" type="number" inputProps={{ min: 1, max: 12 }} value={tpl.teamSize} onChange={(e) => persist({ ...tpl, teamSize: Math.max(1, Math.min(12, +e.target.value || 6)) })} sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: "0.5rem", fontFamily: T.fontBody } }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: "0.72rem", color: T.outlineVariant }}>personen die per week meedoen (auto op laagste saldo)</Typography>
          </Box>
          <CorveeTaskEditor value={tpl} onChange={persist} />
        </>
      )}
    </Box>
  );
}
